import {FormControl, Grid, InputLabel, MenuItem, Select} from "@mui/material";
import VisualizationChart from "../components/visualizationChart";
import Cookies from "js-cookie";
import {routes, useGetDataList} from "../routes/api";
import React, {useEffect, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {convertRecordTime, SelectOnlyDatePicker, sleep} from "../util/util";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

export default function CameraDashboard() {
    const token = Cookies.get('token');
    const {id} = useParams();
    const [sp] = useSearchParams();
    const recordRoute = routes.record;

    const [recordType, setRecordType] = useState('TRFFCCNT');

    const [granularity, setGranularity] = useState('day');
    const [startDate, setStartDate] = useState(sessionStorage.getItem(`cam_${id}_date`) || dayjs().format('YYYY-MM-DD'));

    const {
        data: recordDetail,
        loading: recordLoading,
        statusCode: recordStatus,
        handleCall: recordCall
    } = useGetDataList(token, recordRoute);

    const [allData, setAllData] = useState([]);
    const [noData, setNoData] = useState(false);
    const [countingResults, setCountingResults] = useState([]);
    const [countingDataKey, setCountingDataKey] = useState([]);
    const [speedResults, setSpeedResults] = useState([]);
    const [speedDataKey, setSpeedDataKey] = useState([]);
    const [searching, setSearching] = useState(false);
    const [type, setType] = useState(sessionStorage.getItem(`cam_${id}_type`) || 'vehicle');

    const handleEditDate = (value) => {
        setStartDate(value.format('YYYY-MM-DD'));
        sessionStorage.setItem(`cam_${id}_date`, value.format('YYYY-MM-DD'));
    }

    const fetchRecordDetail = async () => {
        setSearching(true);
        setNoData(false);
        await sleep(1000);
        try {
            await recordCall({
                params: {
                    time_granularity: granularity,
                    r_camera: id,
                    np: 'T',
                    r_type: recordType,
                    r_timeAdded_g: startDate,
                }
            });
        } catch (error) {
            console.error('Error fetching records:', error);
        }
    }

    useEffect(() => {
        fetchRecordDetail();
    }, [startDate]);

    useEffect(() => {
        if (JSON.stringify(recordDetail) !== '{}') {
            const updatedRecords = (recordDetail && recordDetail.length > 0) ? recordDetail.filter((record) => (record.r_timeAdded)).sort((a, b) => new Date(a.r_timeAdded) - new Date(b.r_timeAdded)).map((record) => convertRecordTime(record, granularity)) : [];
            setAllData(updatedRecords);
            if (updatedRecords.length === 0) {
                setNoData(true);
                setCountingResults([]);
                setSpeedResults([]);
                setCountingDataKey([]);
                setSpeedDataKey([]);
                setSearching(false);
                return;
            }
            let tempCountingDataKey = {};
            let tempSpeedDataKey = {};
            setCountingResults(updatedRecords.filter(datum => Object.keys(datum.r_detail).includes('counting_results')).map(datum => (
                Object.entries(datum.r_detail.counting_results).reduce((acc, [key, content]) => ({
                    ...acc, ...Object.entries(content).reduce((newContent, [cKey, cValue]) => {
                        if (!tempCountingDataKey[cKey]) {
                            tempCountingDataKey = {
                                ...tempCountingDataKey,
                                [cKey]: [`${key}, ${cKey}`]
                            };
                        } else if (!tempCountingDataKey[cKey].includes(`${key}, ${cKey}`)) {
                            tempCountingDataKey = {
                                ...tempCountingDataKey,
                                [cKey]: [...tempCountingDataKey[cKey], `${key}, ${cKey}`]
                            };
                        }
                        return {
                            ...newContent,
                            [cKey]: {
                                ...acc[cKey],
                                ...newContent[cKey],
                                [`${key}, ${cKey}`]: cValue
                            }
                        };
                    }, {})
                }), {dateKey: datum.dateKey, toolTipTime: datum.toolTipTime})
            )));
            setSpeedResults(updatedRecords.filter(datum => Object.keys(datum.r_detail).includes('speed_results')).map(datum => (
                Object.entries(datum.r_detail.speed_results).reduce((acc, [key, content]) => ({
                    ...acc, ...Object.entries(content).reduce((newContent, [cKey, cValue]) => {
                        if (!tempSpeedDataKey[cValue.class]) {
                            tempSpeedDataKey = {
                                ...tempSpeedDataKey,
                                [cValue.class]: [`${key}, ${cValue.class}`]
                            };
                        } else if (!tempSpeedDataKey[cValue.class].includes(`${key}, ${cValue.class}`)) {
                            tempSpeedDataKey = {
                                ...tempSpeedDataKey,
                                [cValue.class]: [...tempSpeedDataKey[cValue.class], `${key}, ${cValue.class}`]
                            };
                        }
                        const {class: _, ...tempValue} = cValue;
                        return {
                            ...newContent,
                            [cValue.class]: {
                                ...acc[cValue.class],
                                ...newContent[cValue.class],
                                [`${key}, ${cValue.class}`]: tempValue
                            }
                        };
                    }, {})
                }), {dateKey: datum.dateKey, toolTipTime: datum.toolTipTime})
            )));

            setCountingDataKey(tempCountingDataKey);
            setSpeedDataKey(tempSpeedDataKey);
            setSearching(false);
        }
    }, [recordDetail]);


    const handleTypeChange = (event) => {
        setType(event.target.value);
        sessionStorage.setItem(`cam_${id}_type`, event.target.value);
    };

    if (searching || noData) return (
        <Grid container
              spacing={1}
              sx={{margin: 'auto', mt: 5, width: '80%'}}
              alignItems="flex-start"
              justifyContent="center">
            <Grid size={12}/>
            <Grid size={12}/>
            <Grid size={12}>
                <Typography variant='h4'>Camera {sp.get('cameraSN')} Dashboard</Typography>
            </Grid>
            <Grid size={12}/>
            <Grid size={12} sx={{mb: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <LocalizationProvider dateAdapter={AdapterDayjs} key={'date-select'}>
                    <SelectOnlyDatePicker
                        id='start-date'
                        format='YYYY-MM-DD'
                        name='Date'
                        label='Date'
                        disableFuture
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(value) => handleEditDate(value)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: {
                                    minWidth: {xs: null, md: 150, lg: 200},
                                    backgroundColor: '#FFFFFF',
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center", mt: 15}}>
                {searching && <CircularProgress color="primary" size={40}/>}
                {noData && <Typography variant="button" sx={{fontSize: '1.30rem'}}>
                    No data available for {startDate}
                </Typography>}
            </Grid>
        </Grid>);

    return (
        <Grid container
              spacing={1}
              sx={{margin: 'auto', mt: 5, width: '80%'}}
              alignItems="flex-start"
              justifyContent="center">
            <Grid size={12}/>
            <Grid size={12}/>
            <Grid size={12}>
                <Typography variant='h4'>Camera {sp.get('cameraSN')} Dashboard</Typography>
            </Grid>
            <Grid size={12}/>
            <Grid size={12} sx={{mb: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <LocalizationProvider dateAdapter={AdapterDayjs} key={'date-select'}>
                    <SelectOnlyDatePicker
                        id='start-date'
                        format='YYYY-MM-DD'
                        name='Date'
                        label='Date'
                        disableFuture
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(value) => handleEditDate(value)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: {
                                    minWidth: {xs: null, md: 150, lg: 200},
                                    backgroundColor: '#FFFFFF',
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
                <FormControl variant="outlined" size="small" sx={{ml: 2}}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={type}
                        label="Type"
                        onChange={handleTypeChange}
                        sx={{minWidth: {xs: null, md: 150, lg: 200}}}
                    >
                        <MenuItem value='vehicle'>Vehicles</MenuItem>
                        <MenuItem value='ped'>Pedestrians</MenuItem>
                        <MenuItem value='bicycle'>Cyclists</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            {/*<Grid size={12} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>*/}
            {/*    {searching && <CircularProgress color="primary" size={40}/>}*/}
            {/*</Grid>*/}
            {type === 'vehicle' && <>
                <Grid size={{xs: 12}}>
                    <VisualizationChart
                        title='Vehicle Counts (by type)'
                        data={countingResults}
                        dataKey={countingDataKey}
                        classKey={['car', 'bus', 'truck', 'motorcycle']}
                    />
                </Grid>
                <Grid size={{xs: 12}}>
                    <VisualizationChart
                        title='Vehicle Average Speed (by type)'
                        data={speedResults}
                        dataKey={speedDataKey}
                        valueKey='avg_speed'
                        classKey={['car', 'bus', 'truck', 'motorcycle']}
                    />
                </Grid>
            </>}
            {type === 'ped' && <>
                <Grid size={{xs: 12}}>
                    <VisualizationChart
                        title='Pedestrian Counts'
                        data={countingResults}
                        dataKey={countingDataKey}
                        classKey={['pedestrian']}
                    />
                </Grid>
                <Grid size={{xs: 12}}>
                    <VisualizationChart
                        title='Pedestrian Average Speed'
                        data={speedResults}
                        dataKey={speedDataKey}
                        valueKey='avg_speed'
                        classKey={['pedestrian']}
                    />
                </Grid>
            </>}
            {type === 'bicycle' && <>
                <Grid size={{xs: 12}}>
                    <VisualizationChart
                        title='Cyclist Counts'
                        data={countingResults}
                        dataKey={countingDataKey}
                        classKey={['bicycle']}
                    />
                </Grid>
                {/*<Grid size={{xs: 12}}>*/}
                {/*    <VisualizationChart*/}
                {/*        title='Vehicle Average Speed (by type)'*/}
                {/*        data={speedResults}*/}
                {/*        dataKey={speedDataKey}*/}
                {/*        valueKey='avg_speed'*/}
                {/*        classKey={['car', 'bus', 'truck', 'motorcycle']}*/}
                {/*    />*/}
                {/*</Grid>*/}
            </>}
        </Grid>
    )
}